import React from "react";
import { useState } from "react";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Button,
  TextField,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  CircularProgress,
} from "@mui/material";

interface AddIgnitionRegimeModalProps {
  open: boolean;
  onClose: () => void;
  projectBounds: {
    top: number;
    left: number;
    bottom: number;
    right: number;
  };
  onAdd: (input: {
    name: string;
    mode: "blank" | "grid";
    gridSpacingMeters?: number;
  }) => Promise<void>;
}

const AddIgnitionRegimeModal: React.FC<AddIgnitionRegimeModalProps> = ({
  open,
  onClose,
  onAdd,
  projectBounds,
}) => {
  const [name, setName] = useState("");
  const [mode, setMode] = useState<"blank" | "grid">("blank");
  const [gridSpacingMeters, setGridSpacingMeters] = useState<number>(500);
  const [isLoading, setIsLoading] = useState(false);

  const MAX_IGNITIONS_PER_REGIME = 900;

  const estimateGridIgnitionCount = () => {
    if (mode !== "grid") return 0;
    if (!Number.isFinite(gridSpacingMeters) || gridSpacingMeters <= 0) return 0;

    const { top, bottom, left, right } = projectBounds;
    const latCenter = (top + bottom) / 2;

    // Mirrors backend comment in IgnitionRegimeService.create_default_grid_regime
    // At the equator: 1 degree ≈ 111,320 meters
    const metersPerDegreeLat = 111320.0;
    const metersPerDegreeLng = 111320.0 * Math.cos((latCenter * Math.PI) / 180);
    const latStep = gridSpacingMeters / metersPerDegreeLat;
    const lngStep = gridSpacingMeters / metersPerDegreeLng;

    if (!Number.isFinite(latStep) || !Number.isFinite(lngStep) || latStep <= 0 || lngStep <= 0) {
      return 0;
    }

    const latCount = Math.max(0, Math.ceil((top - bottom) / latStep));
    const lngCount = Math.max(0, Math.ceil((right - left) / lngStep));
    return latCount * lngCount;
  };

  const estimatedIgnitionCount = estimateGridIgnitionCount();
  const boundsInvalid =
    mode === "grid" &&
    (!Number.isFinite(projectBounds.top) ||
      !Number.isFinite(projectBounds.bottom) ||
      !Number.isFinite(projectBounds.left) ||
      !Number.isFinite(projectBounds.right) ||
      projectBounds.top <= projectBounds.bottom ||
      projectBounds.right <= projectBounds.left);
  const gridTooLarge = mode === "grid" && estimatedIgnitionCount > MAX_IGNITIONS_PER_REGIME;
  const gridSpacingInvalid = mode === "grid" && (!Number.isFinite(gridSpacingMeters) || gridSpacingMeters <= 0);
  const canSubmit = !!name.trim() && !gridTooLarge && !gridSpacingInvalid && !boundsInvalid;

  const handleSubmit = async () => {
    if (canSubmit && !isLoading) {
      setIsLoading(true);
      try {
        await onAdd({
          name: name.trim(),
          mode,
          gridSpacingMeters: mode === "grid" ? gridSpacingMeters : undefined,
        });
        setName("");
        setMode("blank");
        setGridSpacingMeters(500);
        onClose();
      } catch (error) {
        console.error("Error adding ignition regime:", error);
      } finally {
        setIsLoading(false);
      }
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Add New Ignition Regime</DialogTitle>
      <DialogContent>
        <TextField
          autoFocus
          margin="dense"
          label="Ignition Regime Name"
          fullWidth
          variant="outlined"
          value={name}
          onChange={(e) => setName(e.target.value)}
        />

        <FormControl sx={{ mt: 2 }}>
          <FormLabel>Regime Type</FormLabel>
          <RadioGroup
            value={mode}
            onChange={(e) => setMode(e.target.value as "blank" | "grid")}
            row
          >
            <FormControlLabel value="blank" control={<Radio />} label="Blank" />
            <FormControlLabel value="grid" control={<Radio />} label="Gridded" />
          </RadioGroup>
        </FormControl>

        {mode === "grid" && (
          <TextField
            margin="dense"
            label="Grid spacing (meters)"
            fullWidth
            variant="outlined"
            type="number"
            value={gridSpacingMeters}
            onChange={(e) => setGridSpacingMeters(Number(e.target.value))}
            error={gridTooLarge || gridSpacingInvalid || boundsInvalid}
            helperText={
              boundsInvalid
                ? "Project bounds are not available yet. Please wait for the project to load."
                : gridSpacingInvalid
                  ? "Grid spacing must be greater than 0"
                  : gridTooLarge
                    ? `Grid would create ${estimatedIgnitionCount} ignitions (max ${MAX_IGNITIONS_PER_REGIME}). Increase spacing.`
                    : `Estimated ignitions: ${estimatedIgnitionCount} (max ${MAX_IGNITIONS_PER_REGIME})`
            }
          />
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="primary">
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          color="primary"
          disabled={!canSubmit || isLoading}
          startIcon={isLoading ? <CircularProgress size={16} color="inherit" /> : undefined}
        >
          {isLoading ? "Adding..." : "Add"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddIgnitionRegimeModal;
